import React, { useState, useEffect } from 'react';
import { 
  FiPieChart, 
  FiBarChart2, 
  FiPackage, 
  FiShoppingCart,
  FiRefreshCw,
  FiDownload,
  FiFilter,
  FiSearch,
  FiCalendar,
  FiToggleLeft,
  FiToggleRight
} from 'react-icons/fi';
import ExcelJS from 'exceljs';
import './Admin.css';

const ThongKeBanhTrungThu = () => {
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('byStore'); // 'byStore' or 'byCake'
  const [filters, setFilters] = useState({
    searchText: ''
  });
  const [summary, setSummary] = useState({
    totalCakes: 0,
    totalQuantity: 0,
    storeCount: 0,
    centralWarehouse: 0
  });
  const [storesData, setStoresData] = useState([]);

  // Fetch inventory data when component mounts
  useEffect(() => {
    fetchStoresData();
    fetchInventoryData();
  }, []);

  // Fetch stores data from API
  const fetchStoresData = async () => {
    try {
      const response = await fetch('https://doticom.vn:3000/stores');
      if (response.ok) {
        const data = await response.json();
        setStoresData(data);
      } else {
        console.error('Không thể tải dữ liệu cửa hàng');
        setStoresData([]);
      }
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu cửa hàng:', error);
      setStoresData([]);
    }
  };

  // Get store name by MaCH
  const getStoreName = (maCH) => {
    const store = storesData.find(store => store.IDCH === maCH);
    return store ? store.TenCuaHang : `Chi nhánh ${maCH}`;
  };

  // Fetch inventory data from API
  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://doticom.vn:3000/api/thongkett');
      
      if (response.ok) {
        const data = await response.json();
        setInventoryData(data);
        
        // Calculate summary statistics
        const totalQuantity = data.tong_con_lai || 0;
        const centralWarehouse = data.kho_dieu_phoi?.tong_con_lai || 0;
        const storeCount = data.chi_nhanh?.length || 0;
        
        // Count unique cake types across all stores
        const allCakes = new Set();
        
        // Add cakes from central warehouse
        if (data.kho_dieu_phoi?.danh_sach_banh) {
          data.kho_dieu_phoi.danh_sach_banh.forEach(cake => allCakes.add(cake.MaSP));
        }
        
        // Add cakes from branches
        if (data.chi_nhanh) {
          data.chi_nhanh.forEach(store => {
            if (store.danh_sach_banh) {
              store.danh_sach_banh.forEach(cake => allCakes.add(cake.MaSP));
            }
          });
        }
        
        setSummary({
          totalCakes: allCakes.size,
          totalQuantity,
          storeCount: storeCount + 1, // +1 for central warehouse
          centralWarehouse
        });
      } else {
        console.error('Không thể tải dữ liệu bánh trung thu');
        setInventoryData(null);
        setSummary({ totalCakes: 0, totalQuantity: 0, storeCount: 0, centralWarehouse: 0 });
      }
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu bánh trung thu:', error);
      setInventoryData(null);
      setSummary({ totalCakes: 0, totalQuantity: 0, storeCount: 0, centralWarehouse: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get all cakes data for filtering and display
  const getAllCakesData = () => {
    if (!inventoryData) return [];
    
    const allCakes = [];
    
    // Add cakes from central warehouse
    if (inventoryData.kho_dieu_phoi?.danh_sach_banh) {
      inventoryData.kho_dieu_phoi.danh_sach_banh.forEach(cake => {
        allCakes.push({
          ...cake,
          MaCH: inventoryData.kho_dieu_phoi.MaCH,
          TenCH: 'Kho điều phối',
          SoLuong: cake.con_lai
        });
      });
    }
    
    // Add cakes from branches
    if (inventoryData.chi_nhanh) {
      inventoryData.chi_nhanh.forEach(store => {
        if (store.danh_sach_banh) {
          store.danh_sach_banh.forEach(cake => {
            allCakes.push({
              ...cake,
              MaCH: store.MaCH,
              TenCH: getStoreName(store.MaCH),
              SoLuong: cake.con_lai
            });
          });
        }
      });
    }
    
    return allCakes;
  };

  // Group data by store
  const getDataGroupedByStore = () => {
    if (!inventoryData) return [];
    
    const storeGroups = [];
    
    // Add central warehouse
    if (inventoryData.kho_dieu_phoi?.danh_sach_banh) {
      storeGroups.push({
        storeId: inventoryData.kho_dieu_phoi.MaCH,
        storeName: 'Kho điều phối',
        cakes: inventoryData.kho_dieu_phoi.danh_sach_banh.map(cake => ({
          ...cake,
          SoLuong: cake.con_lai
        }))
      });
    }
    
    // Add branches
    if (inventoryData.chi_nhanh) {
      inventoryData.chi_nhanh.forEach(store => {
        if (store.danh_sach_banh) {
          storeGroups.push({
            storeId: store.MaCH,
            storeName: getStoreName(store.MaCH),
            cakes: store.danh_sach_banh.map(cake => ({
              ...cake,
              SoLuong: cake.con_lai
            }))
          });
        }
      });
    }
    
    return storeGroups;
  };

  // Group data by cake type
  const getDataGroupedByCake = () => {
    if (!inventoryData) return [];
    
    const cakeGroups = {};
    
    // Process central warehouse
    if (inventoryData.kho_dieu_phoi?.danh_sach_banh) {
      inventoryData.kho_dieu_phoi.danh_sach_banh.forEach(cake => {
        if (!cakeGroups[cake.MaSP]) {
          cakeGroups[cake.MaSP] = {
            cakeId: cake.MaSP,
            cakeName: cake.TenSP,
            stores: []
          };
        }
        cakeGroups[cake.MaSP].stores.push({
          storeId: inventoryData.kho_dieu_phoi.MaCH,
          storeName: 'Kho điều phối',
          quantity: cake.con_lai
        });
      });
    }
    
    // Process branches
    if (inventoryData.chi_nhanh) {
      inventoryData.chi_nhanh.forEach(store => {
        if (store.danh_sach_banh) {
          store.danh_sach_banh.forEach(cake => {
            if (!cakeGroups[cake.MaSP]) {
              cakeGroups[cake.MaSP] = {
                cakeId: cake.MaSP,
                cakeName: cake.TenSP,
                stores: []
              };
            }
            cakeGroups[cake.MaSP].stores.push({
              storeId: store.MaCH,
              storeName: getStoreName(store.MaCH),
              quantity: cake.con_lai
            });
          });
        }
      });
    }
    
    return Object.values(cakeGroups);
  };

  // Filter grouped data based on search text
  const getFilteredGroupedData = () => {
    if (viewMode === 'byStore') {
      const storeGroups = getDataGroupedByStore();
      if (!filters.searchText) return storeGroups;
      
      const searchLower = filters.searchText.toLowerCase();
      return storeGroups.map(store => ({
        ...store,
        cakes: store.cakes.filter(cake => 
          (cake.TenSP && cake.TenSP.toLowerCase().includes(searchLower)) ||
          (cake.MaSP && cake.MaSP.toLowerCase().includes(searchLower))
        )
      })).filter(store => store.cakes.length > 0);
    } else {
      const cakeGroups = getDataGroupedByCake();
      if (!filters.searchText) return cakeGroups;
      
      const searchLower = filters.searchText.toLowerCase();
      return cakeGroups.filter(cake => 
        (cake.cakeName && cake.cakeName.toLowerCase().includes(searchLower)) ||
        (cake.cakeId && cake.cakeId.toLowerCase().includes(searchLower))
      );
    }
  };

  // Format number
  const formatNumber = (num) => {
    return new Intl.NumberFormat('vi-VN').format(num || 0);
  };

  // Export to Excel
  const exportToExcel = async () => {
    if (!inventoryData) {
      alert('Không có dữ liệu để xuất báo cáo');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Thống kê bánh trung thu');

    // Set column widths
    worksheet.columns = [
      { width: 8 },   // STT
      { width: 15 },  // Mã SP
      { width: 40 },  // Tên SP
      { width: 15 },  // Số lượng tồn
      { width: 20 },  // Cửa hàng
    ];

    // Title
    worksheet.mergeCells('A1:E1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'THỐNG KÊ TỒN KHO BÁNH TRUNG THU';
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    // Date
    worksheet.mergeCells('A2:E2');
    const dateCell = worksheet.getCell('A2');
    dateCell.value = `Ngày xuất báo cáo: ${new Date().toLocaleDateString('vi-VN')}`;
    dateCell.font = { size: 10, italic: true };
    dateCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.addRow([]);

    // Header
    const headerRow = worksheet.addRow([
      'STT', 'Mã sản phẩm', 'Tên sản phẩm', 'Số lượng tồn', 'Cửa hàng'
    ]);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
    headerRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE6E6FA' }
    };

    // Data rows
    const allCakes = getAllCakesData();
    allCakes.forEach((item, index) => {
      const dataRow = worksheet.addRow([
        index + 1,
        item.MaSP,
        item.TenSP,
        item.SoLuong || 0,
        item.TenCH
      ]);

      // Format numbers
      dataRow.getCell(4).numFmt = '#,##0';
    });

    // Summary row
    const totalRow = worksheet.addRow([
      '', '', 'TỔNG CỘNG',
      summary.totalQuantity,
      ''
    ]);
    totalRow.font = { bold: true };
    totalRow.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFCCCC' }
    };
    totalRow.getCell(4).numFmt = '#,##0';

    // Export file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TonKhoBanhTrungThu_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-layout">
      <div className="admin-main" style={{ marginLeft: 0 }}>
        {/* Header */}
        <header className="admin-header">
          <div className="header-left">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <FiPieChart size={32} color="#495057" />
              <div>
                <h1>Tồn Kho Bánh Trung Thu</h1>
                <p>Xem thống kê tồn kho bánh trung thu tại các cửa hàng</p>
              </div>
            </div>
          </div>
          <div className="header-right">
            <button
              className="btn btn-primary"
              onClick={exportToExcel}
              disabled={!inventoryData}
            >
              <FiDownload style={{ marginRight: '8px' }} />
              Xuất Excel
            </button>
          </div>
        </header>

        {/* Filters */}
        <div className="content-section">
          <div className="card">
            <div className="card-header">
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiFilter />
                Bộ lọc dữ liệu
              </h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label htmlFor="searchText">Tìm kiếm sản phẩm</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        id="searchText"
                        className="form-control"
                        placeholder="Nhập tên hoặc mã sản phẩm..."
                        value={filters.searchText}
                        onChange={(e) => handleFilterChange('searchText', e.target.value)}
                        style={{ paddingLeft: '35px' }}
                      />
                      <FiSearch 
                        style={{ 
                          position: 'absolute', 
                          left: '10px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          color: '#6c757d'
                        }} 
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Chế độ hiển thị</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                      {viewMode === 'byStore' ? <FiToggleLeft size={24} /> : <FiToggleRight size={24} />}
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => setViewMode(viewMode === 'byStore' ? 'byCake' : 'byStore')}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          minWidth: '200px',
                          justifyContent: 'center'
                        }}
                      >
                        {viewMode === 'byStore' ? 'Theo cửa hàng' : 'Theo loại bánh'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>&nbsp;</label>
                    <button
                      className="btn btn-primary"
                      onClick={fetchInventoryData}
                      disabled={loading}
                      style={{ display: 'block', width: '100%' }}
                    >
                      {loading ? (
                        <>
                          <FiRefreshCw className="spinning" style={{ marginRight: '8px' }} />
                          Đang tải...
                        </>
                      ) : (
                        <>
                          <FiRefreshCw style={{ marginRight: '8px' }} />
                          Tải dữ liệu
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="content-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <FiPackage />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(summary.totalCakes)}</div>
                <div className="stat-label">Tổng loại bánh</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <FiBarChart2 />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(summary.totalQuantity)}</div>
                <div className="stat-label">Tổng số lượng tồn</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <FiShoppingCart />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(summary.centralWarehouse)}</div>
                <div className="stat-label">Kho điều phối</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon">
                  <FiPieChart />
                </div>
              </div>
              <div className="stat-content">
                <div className="stat-value">{formatNumber(summary.storeCount)}</div>
                <div className="stat-label">Số điểm bán</div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Display */}
        <div className="content-section">
          <div className="card">
            <div className="card-header">
              <h3 style={{ margin: 0 }}>
                Chi tiết tồn kho bánh trung thu - {viewMode === 'byStore' ? 'Theo cửa hàng' : 'Theo loại bánh'}
              </h3>
              {getFilteredGroupedData().length > 0 && (
                <span style={{ color: '#6c757d', fontSize: '14px' }}>
                  Hiển thị {getFilteredGroupedData().length} {viewMode === 'byStore' ? 'cửa hàng' : 'loại bánh'}
                </span>
              )}
            </div>
            <div className="card-body">
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FiRefreshCw className="spinning" size={32} color="#007bff" />
                  <p style={{ marginTop: '16px', color: '#6c757d' }}>Đang tải dữ liệu...</p>
                </div>
              ) : !inventoryData ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FiPackage size={48} color="#6c757d" />
                  <h4 style={{ color: '#6c757d', marginTop: '16px' }}>Không có dữ liệu</h4>
                  <p style={{ color: '#6c757d' }}>Không thể tải dữ liệu tồn kho bánh trung thu</p>
                </div>
              ) : getFilteredGroupedData().length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <FiPackage size={48} color="#6c757d" />
                  <h4 style={{ color: '#6c757d', marginTop: '16px' }}>Không tìm thấy</h4>
                  <p style={{ color: '#6c757d' }}>
                    Không tìm thấy dữ liệu phù hợp với từ khóa tìm kiếm
                  </p>
                </div>
              ) : (
                <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                  {viewMode === 'byStore' ? (
                    // Display by Store
                    getFilteredGroupedData().map((store, storeIndex) => (
                      <div key={store.storeId} style={{ 
                        marginBottom: '24px', 
                        border: '1px solid #e3e6f0',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '12px 16px',
                          borderBottom: '1px solid #e3e6f0'
                        }}>
                          <h5 style={{ 
                            margin: 0, 
                            color: '#495057',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <FiShoppingCart />
                            {store.storeName}
                            <span style={{ 
                              fontSize: '12px', 
                              backgroundColor: '#007bff',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px'
                            }}>
                              {store.cakes.length} sản phẩm
                            </span>
                          </h5>
                        </div>
                        <div style={{ padding: '16px' }}>
                          {store.cakes.map((cake, cakeIndex) => (
                            <div key={cake.MaSP} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: cakeIndex < store.cakes.length - 1 ? '1px solid #f1f3f4' : 'none'
                            }}>
                              <div>
                                <span style={{ fontWeight: '500' }}>{cake.TenSP}</span>
                                <span style={{ 
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  color: '#6c757d',
                                  backgroundColor: '#e9ecef',
                                  padding: '2px 6px',
                                  borderRadius: '4px'
                                }}>
                                  {cake.MaSP}
                                </span>
                              </div>
                              <span style={{ 
                                fontWeight: 'bold',
                                color: cake.SoLuong > 0 ? '#28a745' : '#dc3545',
                                fontSize: '16px'
                              }}>
                                {formatNumber(cake.SoLuong || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    // Display by Cake Type
                    getFilteredGroupedData().map((cake, cakeIndex) => (
                      <div key={cake.cakeId} style={{ 
                        marginBottom: '24px', 
                        border: '1px solid #e3e6f0',
                        borderRadius: '8px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '12px 16px',
                          borderBottom: '1px solid #e3e6f0'
                        }}>
                          <h5 style={{ 
                            margin: 0, 
                            color: '#495057',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            <FiPackage />
                            {cake.cakeName}
                            <span style={{ 
                              fontSize: '12px', 
                              backgroundColor: '#e9ecef',
                              color: '#495057',
                              padding: '2px 8px',
                              borderRadius: '4px'
                            }}>
                              {cake.cakeId}
                            </span>
                            <span style={{ 
                              fontSize: '12px', 
                              backgroundColor: '#28a745',
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px'
                            }}>
                              {cake.stores.length} cửa hàng
                            </span>
                          </h5>
                        </div>
                        <div style={{ padding: '16px' }}>
                          {cake.stores.map((store, storeIndex) => (
                            <div key={store.storeId} style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '8px 0',
                              borderBottom: storeIndex < cake.stores.length - 1 ? '1px solid #f1f3f4' : 'none'
                            }}>
                              <span style={{ fontWeight: '500' }}>{store.storeName}</span>
                              <span style={{ 
                                fontWeight: 'bold',
                                color: store.quantity > 0 ? '#28a745' : '#dc3545',
                                fontSize: '16px'
                              }}>
                                {formatNumber(store.quantity || 0)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThongKeBanhTrungThu;
